"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function MerchantNetwork() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="224154" id="responsivefeaturesplit" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div ref={sectionRef} className={`mx-auto max-w-7xl px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-accent/20 shadow-md md:aspect-[4/3] lg:aspect-square">
              <img src="https://stable-sturgeon.10web.cloud/wp-content/uploads/2026/04/Bodyconcept_linda_SchlankimLiegen.jpg" alt="Linda - Ganzheitliche Wohlbefinden Expertin" className="w-full object-cover object-center !h-[500px]" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-accent/20 to-transparent pointer-events-none rounded-lg ring-1 ring-inset ring-border/30"></div>
            </div>
          </div>

          <div className="flex flex-col items-start order-1 lg:order-2">
            <h2 className="mb-6 font-default font-bold leading-tight max-w-2xl text-[rgba(182,4,130,1)] !w-[560px] !text-5xl">Kennst Du das?</h2>
            <p className="mb-8 max-w-lg font-default text-muted-foreground leading-relaxed !w-[600px] !text-lg">Trotz Sport und gesunder Ernährung halten sich hartnäckige Fettpölsterchen an Bauch, Beinen oder Po. Mit der innovativen Technologie von „Abnehmen im Liegen" bieten wir dir eine Methode an, die genau dort ansetzt, wo herkömmliches Training oft an seine Grenzen stößt – und das ganz entspannt ohne Schwitzen.</p>
            <Button variant="outline" size="lg" className="group border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md transition-all duration-300" asChild>
              
            </Button>
          </div>
        </div>
      </div>
    </section>;
}